import { Link } from 'react-router-dom';

interface ArticleCardProps {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  categorySlug: string;
  categoryName: string;
  imageUrl: string;
  date: string;
  views: number;
  comments: number;
  isFeatured?: boolean;
  isHot?: boolean;
  isExclusive?: boolean;
  variant?: 'featured' | 'default' | 'compact';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  categorySlug,
  categoryName,
  imageUrl,
  date,
  views,
  comments,
  isFeatured,
  isHot,
  isExclusive,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link to={`/article/${slug}`} className="group block h-full">
        <article className="relative h-full rounded-xl overflow-hidden bg-background-900">
          <div className="absolute inset-0">
            <img
              src={imageUrl}
              alt={title}
              title={`${title} - 17黑料网`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-transparent"></div>
          </div>
          <div className="relative z-10 p-6 md:p-8 flex flex-col justify-end h-full min-h-[360px]">
            <div className="flex gap-2 mb-3">
              {isExclusive && (
                <span className="px-2.5 py-1 text-xs bg-accent-500 text-background-950 rounded-full font-label font-semibold">
                  独家
                </span>
              )}
              {isHot && (
                <span className="px-2.5 py-1 text-xs bg-red-600 text-white rounded-full font-label font-semibold">
                  热门
                </span>
              )}
            </div>
            <h3 className="font-heading text-lg md:text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors line-clamp-2">
              {title}
            </h3>
            <p className="text-sm text-foreground-300 line-clamp-2 mb-4">{excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>{date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-xs"></i>
                {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line text-xs"></i>
                {comments}
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/article/${slug}`} className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors">
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            title={`${title} - 17黑料网`}
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
            {title}
          </h4>
          <p className="text-xs text-foreground-500 line-clamp-1 mb-2">{excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${slug}`} className="group block h-full">
      <article className="h-full rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            title={`${title} - 17黑料网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 flex gap-1.5">
            {isExclusive && (
              <span className="px-2 py-0.5 text-[10px] bg-accent-500 text-background-950 rounded-full font-label font-semibold">
                独家
              </span>
            )}
            {isHot && (
              <span className="px-2 py-0.5 text-[10px] bg-red-600 text-white rounded-full font-label font-semibold">
                热门
              </span>
            )}
            {isFeatured && (
              <span className="px-2 py-0.5 text-[10px] bg-primary-500 text-white rounded-full font-label font-semibold">
                推荐
              </span>
            )}
          </div>
        </div>
        <div className="p-4 md:p-5">
          <div className="flex items-center gap-2 mb-2">
            <Link
              to={`/category/${categorySlug}`}
              className="text-xs text-primary-500 hover:text-primary-600 font-label transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {categoryName}
            </Link>
          </div>
          <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">{excerpt}</p>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span>{date}</span>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {comments}
              </span>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}

export type { ArticleCardProps };